import React, { ReactNode, useEffect } from "react";
import { datetime } from "../../util/library/datetime";
import { DiseaseService } from "../../util/services/disease";
import { ISiteExtend, SiteService } from "../../util/services/site";
import { showErrorResponse } from "../../util/varibles/global";
import { IDisease, ISite, IUnit } from "../../util/varibles/interface";
import { notify, NotifyCode } from "../../util/varibles/message";
import { createFastContext } from "../createFastContext";

const Init: React.FC<{ id?: string, name?: string, unitId?: string }> = React.memo(({ id, name, unitId }) => {
    const [year, set] = useSiteDetail(state => state.year);
    const get = getSiteDetail();
    useEffect(() => {
        if (id) {
            set({ loadingSite: true });
            SiteService.detail({ id, year: `${year}` })
                .then(rs => {
                    const { units } = rs;
                    const { unit } = get();
                    const selectedId = unit?.id || unitId;
                    set({ site: rs, loadingSite: false, unit: units.find(item => item.id === selectedId) })
                })
                .catch(error => {
                    showErrorResponse(notify[NotifyCode.E39]([name]), error)
                    set({ loadingSite: false })
                })
        }
    }, [get, id, name, set, unitId, year]);

    useEffect(() => {
        set({ diseases: { loading: true } })
        DiseaseService.gets()
            .then(rs => {
                set({ diseases: { loading: false, data: rs } })
            })
            .catch(() => {
                set({ diseases: { loading: false } })
            })
    }, [set]);

    return null;
}, (prev, next) => prev.id === next.id)

interface IState {
    site?: ISiteExtend
    unit?: IUnit
    chart: {
        yLine: any
        yArea: any
        line: any
        area: any
        count: number
        weeks?: number[]
    }
    source: any
    diseases: {
        loading: boolean
        data?: IDisease[]
    }
    loadingSite: boolean
    loadingChart: boolean
    year: string

    onUpdate?(value: ISite): void
}

const {
    Provider,
    useStore: useSiteDetail,
    setStore: setSiteDetail,
    getStore: getSiteDetail,
    callEvent: callEventSiteDetail
} = createFastContext<IState, {
    getSite(args?: { success: (value: ISiteExtend) => void }): void,
    getChart(): void
}>({
    chart: { yLine: {}, yArea: {}, line: [], area: [], count: 0 },
    source: {},
    diseases: {
        loading: false,
    },
    loadingSite: false,
    loadingChart: false,
    year: `${datetime().year}`,
});

const SiteDetailProvider = ({ children, ...args }: {
    children: ReactNode,
    unitId?: string
    data: { id: string, name: string }
} & Partial<IState>) => {
    const { data, unitId } = args;

    return <Provider
        props={{ ...args }}
        event={{
            getSite: (args, event) => {
                const props = { ...args, ...event };
                const { success = (props: ISiteExtend) => props, get, set } = props;
                const { year } = get();
                set({ loadingSite: true });
                SiteService.detail({ id: data.id, year: `${year}` })
                    .then(rs => {
                        success(rs);
                        set({ site: rs, loadingSite: false, })
                    })
                    .catch(error => {
                        showErrorResponse(notify[NotifyCode.E39]([data.name]), error)
                        set({ loadingSite: false })
                    })
            },
            getChart: ({ set, get }) => {
                const { year, unit } = get();
                set(({ source: prevSource, chart: prevChart }) => {
                    const state = { loadingChart: true, source: {}, prevChart }
                    if (Object.keys(prevSource).length > 0) {
                        state.prevChart.line = [];
                        state.prevChart.area = [];
                    }
                    return state
                });
                const id = unit?.id || data.id;
                SiteService.getUnitChart({ id, year })
                    .then(rs => {
                        set({ loadingChart: false, source: rs })
                    })
                    .catch(error => {
                        showErrorResponse('Retrieving chart data failed', error)
                        set({ loadingChart: false })
                    })
            }
        }}
    >
        <Init id={data.id} name={data.name} unitId={unitId} />
        {children}
    </Provider>
}


export { callEventSiteDetail, getSiteDetail, setSiteDetail, SiteDetailProvider, useSiteDetail };
